import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const UsersSchema = z.object({
  min: z.string().optional(),
  max: z.string(),
});

const BenefitsShema = z.object({
  label: z.string().optional(),
  details: z.array(z.string()),
});

const PlanSchema = z
  .object({
    product_id: z.string().optional(),
    tier_id: z.string().optional(),
    type: z.string().optional(),
    description: z.string().optional(),
    price: z.number().optional(),
    count: z.number().optional(),
    users: UsersSchema.optional(),
    body: z.array(z.string()).optional(),
    benefits: z.array(BenefitsShema).optional(),
  })
  .nullable();

const SubscriptionFormSchema = z.object({
  core: PlanSchema,
  progress: PlanSchema,
  taxplan: PlanSchema,
  engage: PlanSchema,
  protect: PlanSchema,
  coupon: z.string(),
});

export type PlanForm = z.infer<typeof PlanSchema>;

export type SubscriptionForm = z.infer<typeof SubscriptionFormSchema>;

export const signupFormDefaultValues: SubscriptionForm = {
  coupon: '',
  core: {
    product_id: '1',
    tier_id: '1',
    type: 'Lite',
    description: 'For Smaller Firms',
    price: 320,
    users: {
      max: '3',
    },
    body: [
      'Leap years ahead.',
      'Monetise your advice.',
      'Best practice everything.',
    ],
    benefits: [
      {
        label: 'Marketing',
        details: ['12 Month Marketing Plan'],
      },
      {
        label: 'Engagement',
        details: [
          'Engagement letters',
          '40+ service descriptions',
          'Pricing recommendations',
        ],
      },
      {
        label: 'Production/Delivery',
        details: [
          'New client set up system',
          'Annual accounts system',
          'Annual tax return system',
          'Company tax return checklist',
          'Company accounts checklist',
          'Individual tax return checklist',
          'BAS workpapers',
          'FBT template',
          'Trust distribution templates',
          'Dividend statement templates',
        ],
      },
      {
        label: 'Completion',
        details: ['PDF editor for mail merge', 'PDF doc packs'],
      },
    ],
  },
  progress: {
    product_id: '2',
    tier_id: '7',
    type: 'Lite',
    description: 'For Smaller Firms',
    price: 320,
    users: {
      max: '15',
    },
    body: [
      'Leap years ahead.',
      'Monetise your advice.',
      'Best practice everything.',
    ],
    benefits: [
      {
        label: '',
        details: [
          'One-touch reporting in seconds',
          'Group position reports in summary',
          'Tax position reports',
          'Payroll and business performance',
          'Annual meeting agendas',
          'ATO benchmarking data integrated',
          'Group average tax calculations',
          'Rank clients to spark conversation',
        ],
      },
    ],
  },
  taxplan: {
    product_id: '3',
    tier_id: '1',
    type: 'Pro',
    description: 'For Smaller Firms',
    price: 540,
    users: {
      max: '10',
    },
    body: [
      'Everything you need to get started, plus:',
      'Advanced client reports.',
      'Tax flow reports.',
      'Show strategy savings.',
    ],
    benefits: [
      {
        label: '',
        details: [
          '90 pre-written tax strategy descriptions in client friendly language',
          'Show strategy savings',
          'Tax Calculation Workpaper',
          'Compare your scenarios and show your clients the dollars saved in your recommended strategy',
          '17 x information-only pieces of value',
          'Standardised tax planning template',
          'Unlimited distribution scenarios',
          'Unlimited Scenarios',
          'Primary producer strategies',
          'Integration with XPM and Xero Tax',
          'Annual budget and stimulus updates',
          'Client advice sign off form',
        ],
      },
      {
        label: 'REPORTS',
        details: [
          'TaxPlan workbook calculation report in Excel',
          'Advanced TaxPlan client report',
          'Tax flow report - Automated 18 month PAYG projections',
          'Scenario comparison report',
        ],
      },
      {
        label: 'MARKETING',
        details: ['Tax planning marketing system'],
      },
      {
        label: 'SUPPORT',
        details: ['One annual training session for your practice'],
      },
    ],
  },
  engage: {
    product_id: '4',
    tier_id: '1',
    type: 'Lite',
    description: 'For Smaller Firms',
    price: 110,
    users: {
      max: '3',
    },
    body: ['Protect and save.', 'Get fees funded.', 'Integrate BAS services.'],
    benefits: [
      {
        label: '',
        details: [
          '50+ service descriptions',
          'Based on CA/IPA/CPA',
          'Automated ethical letters',
          'Value plan price methodology',
          'Upfront/Monthly/Hourly rates',
          'Unlimited entities',
          'Go Cardless direct debits',
          'Email as PDF or Digital Signatures',
        ],
      },
    ],
  },
  protect: {
    product_id: '6',
    tier_id: '1',
    type: 'Pro',
    description: 'For Smaller Firms',
    price: 500,
    users: {
      max: '10',
    },
    body: ['Includes up to 25 + 1 Client Advice Reports'],
    benefits: [
      {
        label: 'Personalised Client Advice Report',
        details: [
          'Customisable Cover Letter',
          'Overview of Asset Protection',
          "Client's Objectives and Concerns",
          'Wealth Transfer and Asset Protection Plans',
          'Explanation of Key Concepts',
          'Recommendations and Associated Fees',
          'Strategy Allocation',
        ],
      },
      {
        label: 'Report Generation & Management',
        details: [
          'Strategy Guidelines in Reports',
          'Step-by-Step Report Wizard',
          'Direct Strategy Assignment',
          'Saved Report Modification Capability',
          'Fee Customisation',
        ],
      },
      {
        label: 'Client and Group Management',
        details: [
          'Client Group Manager',
          'Import from XPM',
          'Include Assets and Liabilities',
        ],
      },
      {
        label: 'Comprehensive Resources',
        details: [
          'Service Delivery System',
          'VPP Client Service Offer Email',
          'Structuring Advice for Strategies',
        ],
      },
      {
        label: 'Presentation and Communication',
        details: ['‘Client View’ Presentation Mode', 'Export to MS Word'],
      },
    ],
  },
};

export const useSubscriptionForm = () => {
  const form = useForm({
    resolver: zodResolver(SubscriptionFormSchema),
    defaultValues: signupFormDefaultValues,
  });

  return form;
};
